import { useMutationState, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  Check,
  Clock,
  Cloud,
  Copy,
  ExternalLink,
  FileText,
  Hash,
  Layers,
  Loader2,
  Mail,
  Rocket,
  Sparkles,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Github } from "@/components/icons/github";
import { MemberSearchCombobox } from "@/components/member-search-combobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeployIdentityScopeDialog } from "@/features/home/deploy-identity-scope-dialog";
import {
  addProjectMember,
  deleteProjectAzure,
  deleteProjectGithub,
  getProjectLogs,
  getProjectResourcesStatus,
  isApiError,
  listProjectMembers,
  type ProjectMember,
  type ProjectSummary,
  promoteProjectMutationKey,
  type PromoteProjectRequest,
  queryKeys,
  removeProjectMember,
  type ResourceDeleteResponse,
  unlinkProjectResource,
  useLinkLangfuseProject,
  useMigrateDeployIdentity,
  useProjectEnvironments,
  usePromoteProject,
  useRedeployProjectEnvironment,
  type UserSearchResult,
} from "@/lib/api";
import type { ProjectLogsResponse, ResourceStatusMap } from "@/lib/local-types";
import { logger } from "@/lib/logger";
import { getEffectiveStatus, hasFailedDeploy } from "@/lib/project-status";
import { useUser } from "@/lib/user-context";
import { cn } from "@/lib/utils";

import { AppRegistrationRequestsSection } from "./app-registration-requests-section";
import { GroupSelect } from "./group-select";
import { InstructionsDialog } from "./instructions-dialog";
import { InviteMemberDialog } from "./invite-member-dialog";
import { ProjectServicesSection } from "./project-services";
import { ProjectSignInSection } from "./project-sign-in-section";
import { ScaffoldProgress, StepLogs } from "./scaffold-progress";

const BASE_STEPS = [
  { key: "creating_repo", label: "Creating GitHub repository" },
  { key: "scaffolding", label: "Running scaffold template" },
  { key: "preparing", label: "Resolving backend dependencies" },
] as const;

const PUSH_STEP = { key: "pushing", label: "Pushing to GitHub" } as const;
const INSTRUCTIONS_STEP = {
  key: "generating_instructions",
  label: "Creating setup instructions",
} as const;

const STEPS = [...BASE_STEPS, PUSH_STEP, INSTRUCTIONS_STEP];

const IN_PROGRESS_STATUSES = new Set([
  "pending",
  "creating_repo",
  "scaffolding",
  "preparing",
  "pushing",
  "generating_instructions",
]);

interface ProjectDetailDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Owner asked to remove the project; the caller runs the removal (see `useRemoveProject`). */
  onRemoveProject?: (project: ProjectSummary) => void;
  onResourceUpdated?: (projectId: string | number, updates: Partial<ProjectSummary>) => void;
  tab?: string;
  onTabChange?: (tab: string) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return "\u2014";
  }
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ProjectDetailDialog({
  project,
  open,
  onOpenChange,
  onRemoveProject,
  onResourceUpdated,
  tab,
  onTabChange,
}: ProjectDetailDialogProps) {
  const { user } = useUser();
  const [localProject, setLocalProject] = useState<ProjectSummary | null>(project);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersFetchError, setMembersFetchError] = useState<string | null>(null);
  const [membersAccessDenied, setMembersAccessDenied] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteInitialEmail, setInviteInitialEmail] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState("");
  const ownerByProject = !!user?.email && project?.owner_email === user.email;
  const ownerByMembers = members.some((m) => m.role === "owner" && m.user_id === user?.id);
  const isProjectOwner = ownerByProject || ownerByMembers;
  const isAdmin = user?.role === "admin";
  const canManageResources = isProjectOwner || isAdmin;
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const activeTab = tab ?? "details";
  const handleTabChange = useCallback(
    (newTab: string) => {
      onTabChange?.(newTab);
    },
    [onTabChange],
  );

  useEffect(() => {
    // Mirror the parent's project prop into local state so optimistic updates
    // (member add/remove, name edits) can mutate without affecting the parent
    // until the next refresh. Sync on prop change.

    setLocalProject(project);
  }, [project]);

  const fetchMembers = useCallback(async () => {
    if (!project) {
      return;
    }
    setMembersLoading(true);
    setMembersFetchError(null);
    setMembersAccessDenied(false);
    try {
      const { data } = await listProjectMembers({ path: { project_id: project.id } });
      setMembers(data ?? []);
    } catch (err) {
      logger.error("[project-detail] failed to fetch members:", err);
      const status = isApiError(err) ? err.status : undefined;
      if (status === 403 || status === 404) {
        setMembersAccessDenied(true);
        setMembersFetchError("You don't have access to this project's members.");
      } else {
        setMembersFetchError("Failed to load members. Please try again.");
      }
    } finally {
      setMembersLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (!open || !project) {
      // Reset member list state when dialog closes. setState in cleanup
      // would be cleaner but the next effect run also needs the cleared
      // state, so we keep it inline with a targeted disable.

      setMembers([]);

      setMemberError("");

      setMembersFetchError(null);

      setMembersAccessDenied(false);
      return;
    }

    fetchMembers();
  }, [open, project, fetchMembers]);

  const handleAddMember = async (selectedUser: UserSearchResult) => {
    if (!project) {
      return;
    }
    setAddingMember(true);
    setMemberError("");

    try {
      await addProjectMember({
        path: { project_id: project.id },
        body: { email: selectedUser.email },
      });
      await fetchMembers();
    } catch (err: unknown) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      if (detail === "User is already a member") {
        setMemberError("User is already a member.");
      } else {
        setMemberError("Failed to add member.");
      }
      logger.error("[project-detail] add member failed:", err);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!project) {
      return;
    }
    try {
      await removeProjectMember({
        path: { project_id: project.id, member_user_id: memberId },
      });
      await fetchMembers();
    } catch (err) {
      logger.error("[project-detail] remove member failed:", err);
    }
  };

  if (!project) {
    return null;
  }

  const p = localProject ?? project;

  const status = getEffectiveStatus(p);

  // NOR-350: a copy-pastable prompt the user drops into Claude (Desktop) to
  // clone the new repo and run first-time setup -- the CLI-native replacement
  // for setup-wrapper skills (champions use Claude Desktop, not the CLI).
  const setupPrompt = p.github_url
    ? `Clone my new project from ${p.github_url}, then set it up for me: run \`make prepare\` to install everything and \`make dev\` to start it, and tell me the URL to open in my browser.`
    : "";

  function copySetupPrompt() {
    if (!setupPrompt) {
      return;
    }
    navigator.clipboard.writeText(setupPrompt);
    toast.success("Prompt copied — paste it into Claude to set up your project");
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="sr-only">
            <DialogTitle>{p.project_name}</DialogTitle>
            <DialogDescription>Project details</DialogDescription>
          </DialogHeader>

          <DialogBody className="pt-6">
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 pr-6">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  {p.app_url && edgeAllowsOpen(p) && (
                    <a
                      href={p.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-accent text-accent-foreground mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm transition-opacity hover:opacity-90"
                    >
                      Open app <ArrowUpRight className="size-3.5" />
                    </a>
                  )}
                  {/* Dev's deploy outcome (NOR-456) -- test/prod get the same
                      badge per-row in EnvironmentsSection below. A failure
                      keeps the last known-good app_url above, so both can
                      show together. */}
                  {hasFailedDeploy(p) && <DeployFailedBadge runUrl={p.deploy_run_url} />}
                  <div className="min-w-0">
                    <h3 className="text-foreground truncate text-base font-bold">
                      {p.project_name}
                    </h3>
                    <p className="text-muted-foreground truncate text-sm">{p.project_slug}</p>
                  </div>
                </div>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList variant="line" className="w-full justify-start">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="logs">Logs</TabsTrigger>
                  <TabsTrigger value="resources">Resources</TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent
                  value="details"
                  forceMount
                  className="space-y-4 pt-4 data-[state=inactive]:hidden"
                >
                  <div className="space-y-3">
                    <DetailRow icon={User} label="Owner">
                      {p.owner_name || p.owner_email || "Unknown"}
                    </DetailRow>
                    <DetailRow icon={Users} label="Group">
                      <GroupSelect
                        projectId={p.id}
                        currentGroupId={p.group_id ?? null}
                        currentGroupName={p.group_name ?? null}
                        editable={canManageResources}
                      />
                    </DetailRow>
                    <DetailRow icon={FileText} label="Name">
                      {p.project_name}
                    </DetailRow>
                    <DetailRow icon={Hash} label="Slug">
                      {p.project_slug}
                    </DetailRow>
                    <DetailRow icon={FileText} label="Desc.">
                      <span className="line-clamp-3">{p.description}</span>
                    </DetailRow>
                    <DetailRow icon={Hash} label="Python">
                      {p.python_version}
                    </DetailRow>
                    <DetailRow icon={Hash} label="Node">
                      {p.node_version}
                    </DetailRow>
                    {p.ai_enabled && (
                      <DetailRow icon={Sparkles} label="AI">
                        <Badge variant="default">AI-enabled</Badge>
                      </DetailRow>
                    )}
                    {p.template_version && (
                      <DetailRow icon={Layers} label="Template">
                        <span className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs">
                          Norican Vibe Template {p.template_version}
                        </span>
                      </DetailRow>
                    )}
                    {p.allowed_email_domains && (
                      <DetailRow icon={Mail} label="Domains">
                        <div className="flex flex-wrap gap-1">
                          {p.allowed_email_domains.split(",").map((domain) => (
                            <span
                              key={domain}
                              className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs"
                            >
                              {domain}
                            </span>
                          ))}
                        </div>
                      </DetailRow>
                    )}
                    {p.error_message && (
                      <DetailRow icon={X} label="Error">
                        <span className="text-destructive">{p.error_message}</span>
                      </DetailRow>
                    )}
                    <DetailRow icon={Clock} label="Date">
                      {formatDate(p.created_at)}
                    </DetailRow>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    {p.github_url && (
                      <a href={p.github_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 size-4" />
                          Open in GitHub
                        </Button>
                      </a>
                    )}
                  </div>

                  {p.status === "completed" && p.github_url && (
                    <div className="space-y-2 rounded-lg border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">Set up in Claude</p>
                        <Button size="sm" variant="outline" onClick={copySetupPrompt}>
                          <Copy className="mr-2 size-3.5" />
                          Copy prompt
                        </Button>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Paste this into Claude to clone and start your project.
                      </p>
                      <pre className="bg-muted/40 overflow-x-auto rounded border p-2 font-mono text-xs whitespace-pre-wrap">
                        {setupPrompt}
                      </pre>
                    </div>
                  )}

                  {p.status === "completed" && (
                    <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                      <Button size="sm" variant="ghost" onClick={() => setInstructionsOpen(true)}>
                        <BookOpen className="mr-2 size-4" />
                        How to get started
                      </Button>
                    </div>
                  )}

                  {(p.edge_hostname || p.edge_status) && (
                    <div className="space-y-1 rounded-lg border p-3">
                      <p className="text-sm font-medium">Web address</p>
                      <EdgeStatusLine
                        hostname={p.edge_hostname}
                        status={p.edge_status}
                        error={p.edge_error}
                        removed={!!p.azure_removed_at}
                      />
                    </div>
                  )}

                  {canManageResources && <EntraGroupsSection project={p} />}
                  {/* Not gated on `canManageResources`: "waiting for IT approval"
                      explains a sign-in that does not work yet to every viewer.
                      Renders nothing for a plain shared-sign-in project. */}
                  <ProjectSignInSection project={p} isAdmin={isAdmin} />

                  {isAdmin && <EnvironmentsSection project={p} />}
                  {isAdmin && p.ai_enabled && <LangfuseSection project={p} />}
                  {canManageResources && p.svc_gateway && (
                    <ProjectServicesSection
                      projectId={p.id}
                      projectName={p.project_name}
                      isAdmin={isAdmin}
                    />
                  )}
                </TabsContent>

                {/* Members Tab */}
                <TabsContent
                  value="members"
                  forceMount
                  className="space-y-4 pt-4 data-[state=inactive]:hidden"
                >
                  <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Users className="text-muted-foreground size-4 shrink-0" />
                      Group
                    </div>
                    <div className="shrink-0">
                      <GroupSelect
                        projectId={p.id}
                        currentGroupId={p.group_id ?? null}
                        currentGroupName={p.group_name ?? null}
                        editable={canManageResources}
                      />
                    </div>
                  </div>

                  {membersLoading ? (
                    <p className="text-muted-foreground py-8 text-center text-sm">Loading...</p>
                  ) : membersFetchError ? (
                    <p className="text-muted-foreground py-8 text-center text-sm">
                      {membersFetchError}
                    </p>
                  ) : (
                    <>
                      {members.length === 0 ? (
                        <p className="text-muted-foreground py-8 text-center text-sm">
                          No members found.
                        </p>
                      ) : (
                        <div className="max-h-60 space-y-1 overflow-y-auto">
                          {members.map((member) => (
                            <div
                              key={member.user_id}
                              className="flex items-center justify-between rounded-md px-2 py-2"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-foreground truncate text-sm font-medium">
                                  {member.full_name || member.email.split("@")[0]}
                                </p>
                                <p className="text-muted-foreground truncate text-xs">
                                  {member.email}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                                  {member.role}
                                </Badge>
                                {canManageResources && member.role !== "owner" && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="size-7"
                                    onClick={() => handleRemoveMember(member.user_id)}
                                  >
                                    <X className="size-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {members.filter((m) => m.role !== "owner").length === 0 &&
                        members.length > 0 && (
                          <p className="text-muted-foreground text-center text-xs">
                            No additional members
                          </p>
                        )}

                      {canManageResources && (
                        <div className="space-y-2">
                          <MemberSearchCombobox
                            excludeIds={members.map((m) => m.user_id)}
                            onSelect={handleAddMember}
                            onInviteClick={(currentQuery) => {
                              setInviteInitialEmail(currentQuery);
                              setInviteDialogOpen(true);
                            }}
                            disabled={addingMember}
                          />
                          {memberError && <p className="text-destructive text-xs">{memberError}</p>}
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Logs Tab */}
                <TabsContent value="logs" forceMount className="pt-4 data-[state=inactive]:hidden">
                  {activeTab === "logs" && (
                    <LogsContent
                      project={p}
                      hasAccess={
                        !membersAccessDenied &&
                        (isProjectOwner ||
                          user?.role === "admin" ||
                          members.some((m) => m.user_id === user?.id))
                      }
                      membersLoading={membersLoading}
                      membersLoadError={!membersAccessDenied && !!membersFetchError}
                    />
                  )}
                </TabsContent>

                {/* Resources Tab */}
                <TabsContent
                  value="resources"
                  forceMount
                  className="pt-4 data-[state=inactive]:hidden"
                >
                  {activeTab === "resources" && (
                    <ResourcesContent
                      project={p}
                      isOwner={canManageResources}
                      onResourceDeleted={(updates) => {
                        setLocalProject((prev) => (prev ? { ...prev, ...updates } : prev));
                        if (project) {
                          onResourceUpdated?.(project.id, updates);
                        }
                      }}
                      onRemoveProject={() => {
                        onOpenChange(false);
                        onRemoveProject?.(p);
                      }}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        initialEmail={inviteInitialEmail}
      />
      <InstructionsDialog
        projectId={project.id}
        open={instructionsOpen}
        onOpenChange={setInstructionsOpen}
      />
    </>
  );
}

/**
 * The backend's plain-string `detail` for a failed call, or `fallback` when the
 * error carries none. Every endpoint used here raises `HTTPException` with a
 * string detail (never FastAPI's validation *list*), so this is safe to show.
 */
function getApiErrorDetail(err: unknown, fallback: string): string {
  if (isApiError(err)) {
    const detail = (err.body as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") {
      return detail;
    }
  }
  return fallback;
}

function getPromoteErrorMessage(err: unknown): string {
  return getApiErrorDetail(err, "Failed to promote the project. Please try again.");
}

const ENV_STATUS_LABELS: Record<string, { label: string; variant: BadgeVariantProp }> = {
  promoted: { label: "Promoted", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
};

type SentryLevel = NonNullable<PromoteProjectRequest["sentry_level"]>;

// NOR-409: gradations offered at the promote prompt rather than one
// all-or-nothing switch, in increasing order of what gets captured.
const SENTRY_LEVEL_LABELS: Record<SentryLevel, string> = {
  off: "Off",
  errors: "Errors only",
  errors_performance: "Errors + performance",
  full: "Full (+ session replay)",
};

type BadgeVariantProp = React.ComponentProps<typeof Badge>["variant"];

/** "Deploy failed" indicator for an environment's last reported deploy
 * (NOR-456) -- a link to the failing run when the workflow reported one,
 * otherwise plain text (an older repo, or a run URL the report omitted). */
function DeployFailedBadge({ runUrl }: { runUrl?: string | null }) {
  const content = (
    <>
      <AlertTriangle className="size-3.5" /> Deploy failed
    </>
  );
  if (!runUrl) {
    return (
      <span className="text-destructive mt-0.5 inline-flex shrink-0 items-center gap-1.5 text-sm font-medium">
        {content}
      </span>
    );
  }
  return (
    <a
      href={runUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-destructive mt-0.5 inline-flex shrink-0 items-center gap-1.5 text-sm font-medium hover:underline"
    >
      {content}
    </a>
  );
}

/** How each edge status reads to a user. */
const EDGE_STATUS_LABELS: Record<string, string> = {
  reserved: "Reserved",
  provisioning: "Being set up",
  origin_pending: "Waiting for the first deploy",
  validating: "Certificate being issued",
  serving: "Live",
  unprotected: "Not protected",
  failed: "Setup failed",
  removing: "Being removed",
};

/** An environment behind the edge only gets an "Open app" link once it serves:
 * until then its public address does not answer. Others are unaffected. */
function edgeAllowsOpen(env: { edge_hostname?: string | null; edge_status?: string | null }) {
  return !env.edge_hostname || env.edge_status === "serving";
}

/** One environment's public address: hostname, status, any error, and a note
 * on how long setup takes while it is not live yet. */
function EdgeStatusLine({
  hostname,
  status,
  error,
  removed = false,
}: {
  hostname?: string | null;
  status?: string | null;
  error?: string | null;
  /** The environment was torn down and no deploy has reported success since
   * (that clears `azure_removed_at`): whatever edge state the backend kept is not live. */
  removed?: boolean;
}) {
  if (!hostname && !status) {
    return null;
  }
  const settingUp =
    !removed && !!status && !["serving", "unprotected", "failed", "removing"].includes(status);
  const label = removed ? "Removed" : status && (EDGE_STATUS_LABELS[status] ?? status);
  return (
    <div className="space-y-0.5 text-xs">
      <p className="text-muted-foreground">
        {hostname && <span className="text-foreground font-medium">{hostname}</span>}
        {hostname && label && " · "}
        {label}
      </p>
      {error && <p className="text-destructive break-words">{error}</p>}
      {settingUp && (
        <p className="text-muted-foreground">
          A new web address can take up to 45 minutes to go live.
        </p>
      )}
    </div>
  );
}

/**
 * The project's two Entra security groups and anything that went wrong with
 * them, for its owner and admins only. Group creation, the dev Contributor
 * grant and member mirroring are all best-effort in the backend, so their
 * failures are surfaced here rather than only in a server log. The messages
 * carry a status and error code, never ids or response bodies.
 */
function EntraGroupsSection({ project }: { project: ProjectSummary }) {
  const groups = [
    { name: project.entra_admins_group_name, id: project.entra_admins_group_id },
    { name: project.entra_users_group_name, id: project.entra_users_group_id },
  ].filter((group) => !!group.id);
  // `entra_groups_warning` holds several keyed entries ("[key] sentence",
  // joined by " | "): one line each, without the key.
  const problems = [
    ...(project.entra_groups_warning ?? "").split(" | "),
    project.entra_groups_rbac_error ?? "",
  ]
    .map((message) => message.replace(/^\[[^\]\s]+\] /, "").trim())
    .filter((message) => message.length > 0);
  if (groups.length === 0 && problems.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1 rounded-lg border p-3">
      <p className="text-sm font-medium">Entra groups</p>
      {groups.map((group) => (
        <p key={group.id} className="text-muted-foreground text-xs">
          <span className="text-foreground font-medium">{group.name ?? "Group"}</span>
          {" · "}
          <span className="font-mono">{group.id}</span>
        </p>
      ))}
      {problems.map((message) => (
        <p key={message} className="text-destructive flex items-start gap-1.5 text-xs break-words">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{message}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * Admin-only dev -> test -> prod promotion pipeline (NOR-306-ish). Dev's
 * state comes from the project itself (there's no `project_environments` row
 * for it); test/prod come from `useProjectEnvironments`. Render success from
 * `status`, never from the presence of `pr_url` -- a first promotion creates
 * the branch off the source with nothing to merge, so `pr_url` can be `null`
 * on an otherwise-successful "promoted" row.
 */
function EnvironmentsSection({ project }: { project: ProjectSummary }) {
  const { data: environments = [] } = useProjectEnvironments(project.id);
  const promote = usePromoteProject(project.id);
  const [promotingTarget, setPromotingTarget] = useState<"test" | "prod" | null>(null);
  // NOR-409: "off" declines linking Sentry -- promotion proceeds exactly as
  // before. Shared across the test/prod buttons below; it's the gradation
  // requested for whichever one is clicked next.
  const [sentryLevel, setSentryLevel] = useState<SentryLevel>("off");

  const testEnv = environments.find((e) => e.environment === "test");
  const prodEnv = environments.find((e) => e.environment === "prod");
  const devStatus = getEffectiveStatus(project);

  const canPromoteToTest = project.status === "completed";
  const canPromoteToProd = testEnv?.status === "promoted";

  function handlePromote(target: "test" | "prod") {
    setPromotingTarget(target);
    promote.mutate(
      { target, sentry_level: sentryLevel },
      {
        onSuccess: (data) => {
          // A 200 response always carries a "promoted" row (services/promote.py
          // raises instead of returning on failure) -- `pr_url` is the only
          // thing that varies: `null` when the target branch was just created
          // off the source with nothing to merge yet.
          if (data?.pr_url) {
            toast.success(`Promoted to ${target} — PR opened.`, {
              action: {
                label: "View PR",
                onClick: () => window.open(data.pr_url!, "_blank", "noopener,noreferrer"),
              },
            });
          } else {
            toast.success(`Promoted to ${target} — branch created, nothing to merge yet.`);
          }
        },
        onError: (err) => {
          // A 502 means the essential steps failed but the environment row
          // was still persisted (see services/promote.py) -- the mutation's
          // onSettled refetch already picked that up, so the row below
          // reflects the partial state even though this toast reads as an
          // error.
          toast.error(getPromoteErrorMessage(err));
        },
        onSettled: () => setPromotingTarget(null),
      },
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <p className="text-sm font-medium">Environments</p>
      <div className="space-y-2">
        <EnvironmentRow
          name="Dev"
          badgeLabel={devStatus.label}
          badgeVariant={devStatus.variant}
          appUrl={edgeAllowsOpen(project) ? project.app_url : null}
          deployFailed={hasFailedDeploy(project)}
          deployRunUrl={project.deploy_run_url}
        />
        <EnvironmentRow
          name="Test"
          badgeLabel={
            testEnv ? (ENV_STATUS_LABELS[testEnv.status]?.label ?? testEnv.status) : "Not promoted"
          }
          badgeVariant={
            testEnv ? (ENV_STATUS_LABELS[testEnv.status]?.variant ?? "secondary") : "secondary"
          }
          appUrl={testEnv && edgeAllowsOpen(testEnv) ? testEnv.app_url : null}
          edge={testEnv}
          prUrl={testEnv?.pr_url}
          deployFailed={!!testEnv && hasFailedDeploy(testEnv)}
          deployRunUrl={testEnv?.deploy_run_url}
        />
        <EnvironmentRow
          name="Prod"
          badgeLabel={
            prodEnv ? (ENV_STATUS_LABELS[prodEnv.status]?.label ?? prodEnv.status) : "Not promoted"
          }
          badgeVariant={
            prodEnv ? (ENV_STATUS_LABELS[prodEnv.status]?.variant ?? "secondary") : "secondary"
          }
          appUrl={prodEnv && edgeAllowsOpen(prodEnv) ? prodEnv.app_url : null}
          edge={prodEnv}
          prUrl={prodEnv?.pr_url}
          deployFailed={!!prodEnv && hasFailedDeploy(prodEnv)}
          deployRunUrl={prodEnv?.deploy_run_url}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Label htmlFor="sentry-level" className="text-muted-foreground text-xs font-normal">
          Link Sentry
        </Label>
        <Select value={sentryLevel} onValueChange={(v) => setSentryLevel(v as SentryLevel)}>
          <SelectTrigger id="sentry-level" size="sm" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SENTRY_LEVEL_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          disabled={!canPromoteToTest || promote.isPending}
          onClick={() => handlePromote("test")}
        >
          <Rocket className="mr-2 size-3.5" />
          {promotingTarget === "test"
            ? "Promoting…"
            : testEnv
              ? "Re-promote to test"
              : "Promote to test"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canPromoteToProd || promote.isPending}
          onClick={() => handlePromote("prod")}
        >
          <Rocket className="mr-2 size-3.5" />
          {promotingTarget === "prod"
            ? "Promoting…"
            : prodEnv
              ? "Re-promote to prod"
              : "Promote to prod"}
        </Button>
        {!canPromoteToProd && (
          <p className="text-muted-foreground text-xs">
            Promote to test (and have it succeed) before promoting to prod.
          </p>
        )}
      </div>
      <DeployIdentityRow project={project} />
    </div>
  );
}

/**
 * NOR-471: which Azure identity this project deploys as. Projects scaffolded
 * before NOR-471 share one identity per instance, and Azure caps a managed
 * identity at 20 federated credentials, so an admin moves the legacy ones onto
 * their own identity here, one project at a time. One way: the migration also
 * frees this project's credentials on the shared identity, and nothing in the
 * UI puts it back.
 */
function DeployIdentityRow({ project }: { project: ProjectSummary }) {
  const migrate = useMigrateDeployIdentity(project.id);

  function handleMigrate() {
    migrate.mutate(undefined, {
      onSuccess: (data) => {
        if (!data) {
          return;
        }
        const envs = data.migrated_environments;
        if (envs.length === 0) {
          // `dev` is always attempted, so an empty list means every rewrite
          // failed this time -- still deploying on the shared identity's
          // client id, but safe to retry: the endpoint is idempotent, so the
          // "Re-sync" action below can simply be used again.
          toast.warning(
            `No environment's AZURE_CLIENT_ID could be rewritten -- try again in a moment. ${data.note}.`,
          );
          return;
        }
        // A non-empty `migrated_environments` isn't a plain success on its own
        // (Codex review): an environment whose rewrite failed, whose old
        // shared credential couldn't be freed afterward, or whose Key Vault
        // access policy couldn't be granted (NOR-483 -- the next deploy there
        // will 403 on Terraform's refresh until this is retried) still leaves
        // the migration partial -- surface that as a warning, not a success
        // toast.
        if (
          data.failed_environments.length > 0 ||
          data.credential_cleanup_failures.length > 0 ||
          data.vault_policy_failures.length > 0
        ) {
          const parts = [`Now deploying as ${data.name} (${envs.join(", ")}).`];
          if (data.failed_environments.length > 0) {
            parts.push(`Failed to rewrite: ${data.failed_environments.join(", ")}.`);
          }
          if (data.credential_cleanup_failures.length > 0) {
            parts.push(
              `Couldn't free the old shared credential for: ${data.credential_cleanup_failures.join(", ")}.`,
            );
          }
          if (data.vault_policy_failures.length > 0) {
            parts.push(
              `Couldn't grant Key Vault access for: ${data.vault_policy_failures.join(", ")} -- the next deploy there will fail until this is retried.`,
            );
          }
          parts.push(`${data.note}. Re-sync to retry.`);
          toast.warning(parts.join(" "));
          return;
        }
        toast.success(`Now deploying as ${data.name} (${envs.join(", ")}). ${data.note}.`);
      },
      onError: (err) => {
        toast.error(
          getApiErrorDetail(err, "Failed to migrate the deploy identity. Please try again."),
        );
      },
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-t pt-3">
      <span className="text-muted-foreground text-xs">Deploy identity</span>
      {project.deploy_identity_client_id ? (
        <>
          <span className="text-sm">
            Per-project: {project.deploy_identity_name ?? project.deploy_identity_client_id}
          </span>
          {/* The migrate endpoint is idempotent and safe to re-run at any time
              (it re-verifies/repairs the identity, its grants, its federated
              credentials and every environment's AZURE_CLIENT_ID) -- offered
              here, unconfirmed, as a low-key repair action for the rare case
              where an earlier migration only got partway through one of its
              environments. Nothing to lose by re-running it on a project
              that's already fully in sync. */}
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground h-auto px-1.5 py-0.5 text-xs"
            disabled={migrate.isPending}
            onClick={handleMigrate}
          >
            {migrate.isPending ? "Re-syncing…" : "Re-sync"}
          </Button>
          {/* Subscription-wide grants -> the project's own resource groups (or
              back): a dry run on open, the change only once confirmed. */}
          <DeployIdentityScopeDialog project={project} />
        </>
      ) : (
        <>
          <span className="text-sm">Shared instance identity (legacy)</span>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              {/* A project still being scaffolded is assigned its own identity
                  by the pipeline itself (step 1), so it only reads as "legacy"
                  until that write lands -- don't offer to migrate it. */}
              <Button
                size="sm"
                variant="outline"
                disabled={migrate.isPending || IN_PROGRESS_STATUSES.has(project.status)}
              >
                {migrate.isPending ? "Migrating…" : "Migrate to a per-project identity"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Migrate to a per-project identity?</AlertDialogTitle>
                <AlertDialogDescription>
                  This creates a dedicated Azure deploy identity for this project, points dev and
                  every promoted environment at it, and frees the credentials this project holds on
                  the shared instance identity. Nothing is deleted, but it cannot be reversed from
                  here. Azure role assignments can take a few minutes to propagate, so the next
                  deploy may need a re-run.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleMigrate}>Migrate</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

/**
 * NOR-477: link an AI-enabled project to its own dedicated Langfuse project.
 * Until an admin does, the scaffolded app traces into the shared sandbox
 * project, so the block highlights itself once the project has a `prod`
 * environment (or one is being promoted) and is still unlinked. It never gates
 * promotion -- the promote buttons above work regardless of the link state.
 */
function LangfuseSection({ project }: { project: ProjectSummary }) {
  const { data: environments = [] } = useProjectEnvironments(project.id);
  const link = useLinkLangfuseProject(project.id);
  const [publicKey, setPublicKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [replacingKeys, setReplacingKeys] = useState(false);

  // A promotion running in the sibling EnvironmentsSection: `useMutation`
  // state is per-instance, so read it off the shared mutation key instead.
  const promotingToProd = useMutationState({
    filters: { mutationKey: promoteProjectMutationKey(project.id), status: "pending" },
    select: (mutation) => (mutation.state.variables as { target?: string } | undefined)?.target,
  }).includes("prod");

  const linkedName = project.langfuse_project_name ?? null;
  // Only an actually-promoted prod row counts -- a failed promotion attempt
  // must not light up the "needs attention" highlight (mirrors the backend's
  // own `status === "promoted"` filter for which environments get redeployed).
  const hasProd = environments.some((e) => e.environment === "prod" && e.status === "promoted");
  const needsAttention = !linkedName && (hasProd || promotingToProd);
  const formOpen = !linkedName || replacingKeys;
  const canSubmit = !!publicKey.trim() && !!secretKey.trim() && !link.isPending;

  function closeForm() {
    setPublicKey("");
    setSecretKey("");
    setReplacingKeys(false);
  }

  function handleLink() {
    link.mutate(
      { public_key: publicKey.trim(), secret_key: secretKey.trim() },
      {
        onSuccess: (data) => {
          const redeployed = data?.redeployed_environments ?? [];
          const failed = data?.failed_redeploy_environments ?? [];
          if (failed.length > 0) {
            // The keys are written and persisted either way -- but an
            // environment that failed to redeploy is still running on the
            // old sandbox keys, so this must never read as a plain success.
            const need = failed.length === 1 ? "needs" : "need";
            toast.warning(
              redeployed.length > 0
                ? `Linked. Redeployed ${redeployed.join(", ")}, but ${failed.join(", ")} still ${need} a manual redeploy.`
                : `Linked, but ${failed.join(", ")} still ${need} a manual redeploy.`,
            );
          } else if (redeployed.length > 0) {
            toast.success(`Linked. Redeploying ${redeployed.join(", ")}`);
          } else {
            toast.success("Linked. Redeploy the project to apply the new keys");
          }
          // A services gateway project: dev keeps tracing to the sandbox until fixed.
          if (data?.svc_gateway_warning) {
            toast.warning(data.svc_gateway_warning);
          }
          closeForm();
        },
        onError: (err) => {
          toast.error(
            getApiErrorDetail(err, "Failed to link the Langfuse project. Please try again."),
          );
        },
      },
    );
  }

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border p-3",
        needsAttention && "border-primary bg-primary/5",
      )}
    >
      <p className="text-sm font-medium">Langfuse</p>

      {linkedName ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-muted-foreground text-sm">
            Langfuse project:{" "}
            <code className="bg-muted/40 rounded px-1 font-mono text-xs">{linkedName}</code>, linked{" "}
            {formatDate(project.langfuse_linked_at ?? null)}
          </p>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => (replacingKeys ? closeForm() : setReplacingKeys(true))}
          >
            {replacingKeys ? "Cancel" : "Replace keys"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm">
            Traces of this project go to the shared sandbox project. For production, give it its own
            Langfuse project:
          </p>
          <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
            <li>
              Open cloud.langfuse.com, organisation norican, New project, name it{" "}
              <code className="bg-muted/40 rounded px-1 font-mono text-xs">
                {project.project_slug}
              </code>
              .
            </li>
            <li>In the project&apos;s Settings, API Keys, create a key pair.</li>
            <li>Paste the keys below.</li>
          </ol>
        </div>
      )}

      {formOpen && (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="langfuse-public-key" className="text-muted-foreground text-xs">
              Public key
            </Label>
            <Input
              id="langfuse-public-key"
              value={publicKey}
              onChange={(e) => setPublicKey(e.target.value)}
              placeholder="pk-lf-…"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="langfuse-secret-key" className="text-muted-foreground text-xs">
              Secret key
            </Label>
            <Input
              id="langfuse-secret-key"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder="sk-lf-…"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <Button size="sm" variant="outline" disabled={!canSubmit} onClick={handleLink}>
            <Sparkles className="mr-2 size-3.5" />
            {link.isPending ? "Linking…" : "Link Langfuse project"}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A single environment row in the promotion pipeline (dev/test/prod). */
function EnvironmentRow({
  name,
  badgeLabel,
  badgeVariant,
  appUrl,
  prUrl,
  deployFailed,
  deployRunUrl,
  edge,
}: {
  name: string;
  badgeLabel: string;
  badgeVariant: BadgeVariantProp;
  appUrl?: string | null;
  prUrl?: string | null;
  /** Whether this environment's last reported deploy failed (NOR-456). */
  deployFailed?: boolean;
  deployRunUrl?: string | null;
  /** This environment's public address state, when it has one. Dev's is shown
   * above the pipeline for everyone, so only test/prod pass it here. */
  edge?: {
    edge_hostname?: string | null;
    edge_status?: string | null;
    edge_error?: string | null;
    azure_removed_at?: string | null;
  };
}) {
  return (
    <div className="space-y-1 rounded-md border px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{name}</span>
          <Badge variant={badgeVariant}>{badgeLabel}</Badge>
          {deployFailed && <DeployFailedBadge runUrl={deployRunUrl} />}
        </div>
        <div className="flex items-center gap-2">
          {prUrl && (
            <a href={prUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost">
                <ExternalLink className="mr-1 size-3.5" />
                PR
              </Button>
            </a>
          )}
          {appUrl && (
            <a href={appUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost">
                <ArrowUpRight className="mr-1 size-3.5" />
                Open app
              </Button>
            </a>
          )}
        </div>
      </div>
      <EdgeStatusLine
        hostname={edge?.edge_hostname}
        status={edge?.edge_status}
        error={edge?.edge_error}
        removed={!!edge?.azure_removed_at}
      />
    </div>
  );
}

type RedeployEnvironment = "dev" | "test" | "prod";

/**
 * Redeploy an environment (owner or admin) of a project whose resource groups
 * ScaffoldUI creates. The backend re-creates the environment's resource group
 * and its deploy identity's access, then starts the deploy workflow -- how an
 * environment comes back after an Azure-only teardown. Offered for dev once the
 * scaffold completed and for each promoted environment; the backend decides.
 */
function RedeploySection({ project }: { project: ProjectSummary }) {
  const { data: environments = [] } = useProjectEnvironments(project.id);
  const redeploy = useRedeployProjectEnvironment(project.id);
  const [redeploying, setRedeploying] = useState<RedeployEnvironment | null>(null);

  const available: { environment: RedeployEnvironment; label: string }[] = [
    ...(project.status === "completed" ? [{ environment: "dev" as const, label: "Dev" }] : []),
    ...(["test", "prod"] as const)
      .filter((env) => environments.some((e) => e.environment === env && e.status === "promoted"))
      .map((env) => ({ environment: env, label: env === "test" ? "Test" : "Prod" })),
  ];
  if (available.length === 0) {
    return null;
  }

  function handleRedeploy(environment: RedeployEnvironment) {
    setRedeploying(environment);
    redeploy.mutate(environment, {
      onSuccess: () => {
        toast.success(`Redeploying ${environment} -- the deploy takes a few minutes.`);
      },
      onError: (err) => {
        toast.error(getApiErrorDetail(err, `Failed to redeploy ${environment}. Please try again.`));
      },
      onSettled: () => setRedeploying(null),
    });
  }

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div>
        <p className="text-sm font-medium">Redeploy</p>
        <p className="text-muted-foreground text-xs">
          Re-creates the environment&apos;s Azure resource group and its deploy access, then runs
          its deploy again. Use it after tearing down the Azure resources. One redeploy per minute.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {available.map(({ environment, label }) => (
          <Button
            key={environment}
            size="sm"
            variant="outline"
            disabled={redeploy.isPending}
            onClick={() => handleRedeploy(environment)}
          >
            <Rocket className="mr-2 size-3.5" />
            {redeploying === environment ? "Redeploying…" : `Redeploy ${label}`}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Logs tab content -- live SSE for in-progress, static summary for terminal. */
function LogsContent({
  project,
  hasAccess,
  membersLoading,
  membersLoadError,
}: {
  project: ProjectSummary;
  hasAccess: boolean;
  membersLoading: boolean;
  membersLoadError: boolean;
}) {
  if (membersLoading) {
    return <p className="text-muted-foreground py-8 text-center text-sm">Loading...</p>;
  }

  if (membersLoadError) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Could not verify access. Failed to load member information. Please try again later.
      </p>
    );
  }

  if (!hasAccess) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        You don&apos;t have access to logs for this project. Only project owners and members can
        view logs.
      </p>
    );
  }

  if (IN_PROGRESS_STATUSES.has(project.status)) {
    return (
      <ScaffoldProgress
        projectId={project.id}
        onComplete={() => {
          // The dialog will re-render with updated status on next fetch
        }}
        onFailed={() => {
          // The dialog will re-render with updated status on next fetch
        }}
      />
    );
  }

  return <HistoricalLogs project={project} />;
}

/** Fetches persisted build logs and renders them with collapsible terminals per step. */
function HistoricalLogs({ project }: { project: ProjectSummary }) {
  const [buildLog, setBuildLog] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  useEffect(() => {
    getProjectLogs({ path: { project_id: project.id } })
      .then(({ data }) => setBuildLog((data as ProjectLogsResponse | undefined)?.build_log ?? null))
      .catch(() => setBuildLog(null))
      .finally(() => setLoading(false));
  }, [project.id]);

  const logsByStep = useMemo(() => {
    if (!buildLog) {
      return {};
    }
    const result: Record<string, string[]> = {};
    for (const line of buildLog.split("\n")) {
      const match = line.match(/^\[([^\]]+)\]\s(.*)$/);
      if (match) {
        const step = match[1];
        const text = match[2];
        if (step !== undefined) {
          (result[step] ??= []).push(text ?? "");
        }
      }
    }
    return result;
  }, [buildLog]);

  if (loading) {
    return <p className="text-muted-foreground py-8 text-center text-sm">Loading logs...</p>;
  }

  function toggleStep(key: string) {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div className="space-y-1">
      {STEPS.map((step) => {
        const isFailed = project.status === "failed";
        const isCancelled = project.status === "cancelled";
        const stepLines = logsByStep[step.key] ?? [];

        return (
          <div key={step.key}>
            <div className="flex items-center gap-3 py-1.5">
              <div className="flex size-6 shrink-0 items-center justify-center">
                {isFailed || isCancelled ? (
                  <div className="bg-muted size-2 rounded-full" />
                ) : (
                  <Check className="size-5 text-green-600 dark:text-green-400" />
                )}
              </div>
              <span
                className={
                  isFailed || isCancelled
                    ? "text-muted-foreground text-sm"
                    : "text-sm text-green-700 dark:text-green-400"
                }
              >
                {step.label}
              </span>
            </div>
            {stepLines.length > 0 && (
              <StepLogs
                lines={stepLines}
                open={expandedSteps.has(step.key)}
                onToggle={() => toggleStep(step.key)}
              />
            )}
          </div>
        );
      })}
      {project.status === "failed" && project.error_message && (
        <div className="bg-destructive/10 border-destructive/30 mt-2 rounded-lg border p-3">
          <p className="text-destructive text-sm font-medium">{project.error_message}</p>
        </div>
      )}
      {project.status === "cancelled" && (
        <div className="border-border bg-secondary dark:border-border dark:bg-secondary/30 mt-2 rounded-lg border p-3">
          <p className="text-muted-foreground text-sm font-medium">Cancelled by user</p>
        </div>
      )}
      {!buildLog && (
        <p className="text-muted-foreground py-4 text-center text-xs">
          No detailed logs available for this project.
        </p>
      )}
    </div>
  );
}

// Azure RG deletion is fire-and-forget on the backend and takes several
// minutes; while it runs the status reads "deleting", so poll until it flips
// to "missing" rather than leaving a stale "Connected" (NOR-295). Capped so a
// genuinely stuck teardown can't poll forever with the dialog left open.
const AZURE_POLL_INTERVAL_MS = 5000;
const AZURE_POLL_MAX_ATTEMPTS = 180; // ~15 min

/** Resources tab -- shows connected resources with delete controls. */
function ResourcesContent({
  project,
  isOwner,
  onResourceDeleted,
  onRemoveProject,
}: {
  project: ProjectSummary;
  isOwner: boolean;
  onResourceDeleted: (updates: Partial<ProjectSummary>) => void;
  onRemoveProject: () => void;
}) {
  const queryClient = useQueryClient();
  const [liveStatus, setLiveStatus] = useState<ResourceStatusMap | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [deletingResource, setDeletingResource] = useState<string | null>(null);
  const [error, setError] = useState<{
    message: string;
    manualUrl?: string;
    resourceKey?: string;
    urlFields?: Partial<ProjectSummary>;
  } | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  // True while an Azure teardown is believed in-flight. Drives the poller and
  // the "Deleting..." row -- kept separate from the raw status so a single
  // transient "error" poll can't abandon an in-progress teardown and flip the
  // row back to a misleading "Connected".
  const [azureTearingDown, setAzureTearingDown] = useState(false);

  // Re-fetchable so the Azure teardown poller can refresh the same state; the
  // initial-load spinner is driven separately (only the first fetch).
  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await getProjectResourcesStatus({ path: { project_id: project.id } });
      setLiveStatus((data ?? null) as ResourceStatusMap | null);
    } catch (err) {
      logger.error("[resources] status check failed:", err);
    }
  }, [project.id]);

  useEffect(() => {
    setStatusLoading(true);
    void refreshStatus().finally(() => setStatusLoading(false));
  }, [refreshStatus]);

  // A teardown may already be running when the dialog opens (started earlier or
  // from another session) -- pick it up from the fetched status.
  const azureStatus = liveStatus?.azure;
  useEffect(() => {
    if (azureStatus === "deleting") {
      setAzureTearingDown(true);
    }
  }, [azureStatus]);

  // Poll while a teardown is in flight. Driven by the sticky `azureTearingDown`
  // flag, not the raw status, so a transient "error" tick keeps polling instead
  // of stopping (and misleadingly flipping the row back to "Connected").
  useEffect(() => {
    if (!azureTearingDown) {
      return;
    }
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (attempts > AZURE_POLL_MAX_ATTEMPTS) {
        clearInterval(id);
        setAzureTearingDown(false);
        toast.info("Azure teardown is taking longer than expected — check the Azure portal.");
        return;
      }
      void refreshStatus();
    }, AZURE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [azureTearingDown, refreshStatus]);

  // Resolve the teardown once the RG is confirmed gone (or, unexpectedly, still
  // healthy). Keys off the sticky flag rather than a raw status transition, so
  // an intervening "error" tick can't make it miss the completion toast.
  useEffect(() => {
    if (!azureTearingDown) {
      return;
    }
    if (azureStatus === "missing") {
      toast.success("Azure resources deleted.");
      setAzureTearingDown(false);
    } else if (azureStatus === "active") {
      setAzureTearingDown(false);
    }
  }, [azureTearingDown, azureStatus]);

  const resources = [
    {
      key: "github",
      name: "GitHub Repository",
      icon: Github,
      url: project.github_url,
      included: true, // GitHub is always included
      urlFields: { github_url: null } as Partial<ProjectSummary>,
    },
    {
      key: "azure",
      name: "Azure Resource Group",
      icon: Cloud,
      // Derived on every response, not a stored/nullable DB field (NOR-295)
      // -- `undefined` only when no Azure tenant/subscription is configured.
      url: project.azure_portal_url ?? undefined,
      included: true,
      urlFields: {} as Partial<ProjectSummary>,
    },
  ];

  const deleteResourceByKey = async (
    resourceKey: string,
  ): Promise<ResourceDeleteResponse | undefined> => {
    const path = { project_id: project.id };
    switch (resourceKey) {
      case "github": {
        const { data } = await deleteProjectGithub({ path });
        return data;
      }
      case "azure": {
        const { data } = await deleteProjectAzure({ path });
        return data;
      }
      default:
        throw new Error(`Unknown resource: ${resourceKey}`);
    }
  };

  const handleDeleteResource = async (resourceKey: string, urlFields: Partial<ProjectSummary>) => {
    setDeletingResource(resourceKey);
    setError(null);
    try {
      const result = await deleteResourceByKey(resourceKey);
      if (resourceKey === "azure") {
        // Even a partial teardown may have removed some environments' apps
        // (`DBClient.mark_project_azure_removed`): re-read the project and its
        // environments rather than guess which.
        void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projectEnvironments(project.id),
        });
      }
      if (result?.success) {
        onResourceDeleted(urlFields);
        if (resourceKey === "azure") {
          // Azure's own resource group deletion is fire-and-forget on the
          // backend (`begin_delete`) and takes a few minutes. Flip the sticky
          // teardown flag so the row reads "Deleting..." instantly and the
          // poller takes over, re-checking until the RG is actually gone. The
          // flag alone drives the display, so there's no need to fabricate a
          // status when `liveStatus` hasn't loaded yet.
          setAzureTearingDown(true);
          setLiveStatus((prev) => (prev ? { ...prev, azure: "deleting" } : prev));
          toast.success("Azure teardown started — tracking until it's gone.");
        }
      } else {
        setError({
          message: result?.error || "Failed to delete resource",
          manualUrl: result?.manual_url || undefined,
          resourceKey,
          urlFields,
        });
      }
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError({
        message: detail || "Failed to delete resource",
        resourceKey,
        urlFields,
      });
    } finally {
      setDeletingResource(null);
    }
  };

  // The removal itself runs outside this dialog: teardown can take minutes and
  // the dialog closes at once instead of sitting on a "Removing..." button.
  const handleDeleteProject = () => {
    setError(null);
    onRemoveProject();
  };

  const handleUnlink = async (resourceKey: string, urlFields: Partial<ProjectSummary>) => {
    setUnlinking(true);
    setError(null);
    try {
      const { data: result } = await unlinkProjectResource({
        path: { project_id: project.id, resource_type: resourceKey },
      });
      if (result?.success) {
        onResourceDeleted(urlFields);
      } else {
        setError({ message: result?.error || "Failed to unlink resource" });
      }
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError({ message: detail || "Failed to unlink resource" });
    } finally {
      setUnlinking(false);
    }
  };

  const allResourcesRemoved = !project.github_url;
  // No repository yet is not "removed" while the project is still being
  // created; a failed or cancelled create is settled and can be removed.
  const removeProjectIsLastStep = allResourcesRemoved && !IN_PROGRESS_STATUSES.has(project.status);
  // The row reads "Deleted externally": the way on is "Mark as removed".
  const repoGoneOutsideScaffold = liveStatus?.github === "missing";

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground text-sm">Checking resource status...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resource rows */}
      <div className="space-y-2">
        {resources.map((resource) => {
          const isAzure = resource.key === "azure";
          const live = liveStatus?.[resource.key as keyof ResourceStatusMap];
          const isDeleting = isAzure && (azureTearingDown || live === "deleting");
          const isMissing = live === "missing" && !!resource.url && !isDeleting;
          const resourceStatus = isDeleting
            ? "deleting"
            : isAzure && live === "not_configured"
              ? "not_provisioned"
              : isMissing
                ? "missing"
                : resource.url
                  ? "connected"
                  : resource.included
                    ? "removed"
                    : "not_provisioned";

          return (
            <div
              key={resource.key}
              className="flex items-center justify-between rounded-lg border px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <resource.icon className="text-muted-foreground size-5" />
                <div>
                  <p className="text-sm font-medium">{resource.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {resourceStatus === "connected" && (
                      <span className="text-green-600 dark:text-green-400">Connected</span>
                    )}
                    {resourceStatus === "deleting" && (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <Loader2 className="size-3 animate-spin" />
                        Deleting…
                      </span>
                    )}
                    {resourceStatus === "missing" && (
                      <span className="text-amber-600 dark:text-amber-400">
                        {isAzure ? "Removed" : "Deleted externally"}
                      </span>
                    )}
                    {resourceStatus === "removed" && (
                      <span className="text-muted-foreground">Removed</span>
                    )}
                    {resourceStatus === "not_provisioned" && (
                      <span className="text-muted-foreground">Not provisioned</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {resource.url && !isMissing && (
                  <a href={resource.url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="ghost">
                      <ExternalLink className="mr-1 size-3.5" />
                      Open
                    </Button>
                  </a>
                )}
                {/* GitHub is the only resource with a stored/nullable URL to null out
                    -- Azure has nothing to "mark as removed" (NOR-295). */}
                {isOwner && !isAzure && isMissing && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={unlinking}
                    onClick={() => handleUnlink(resource.key, resource.urlFields)}
                  >
                    {unlinking ? "Unlinking..." : "Mark as removed"}
                  </Button>
                )}
                {isOwner && !isAzure && resource.url && !isMissing && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
                        disabled={deletingResource !== null}
                      >
                        <Trash2 className="mr-1 size-3.5" />
                        {deletingResource === resource.key ? "Deleting..." : "Delete"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {resource.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete the {resource.name} resource. This action
                          cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => handleDeleteResource(resource.key, resource.urlFields)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {/* Azure teardown is destructive in a different way (RG + Entra
                    redirect URI + repo secrets, not just this one card). Hidden
                    once the RG is gone: an in-app teardown already removed all
                    three, so a "Delete" button next to a "Removed" resource just
                    reads as broken (NOR-295). Double confirmation: typing the
                    project name enables the button. */}
                {isOwner && isAzure && !isMissing && (
                  <AzureTeardownTrigger
                    projectName={project.project_name}
                    deleting={deletingResource === "azure" || isDeleting}
                    disabled={deletingResource !== null || isDeleting}
                    onConfirm={() => handleDeleteResource("azure", resource.urlFields)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-700 dark:text-red-200">{error.message}</p>
          <div className="mt-2 flex items-center gap-3">
            {error.manualUrl && (
              <a
                href={error.manualUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-red-700 underline dark:text-red-200"
              >
                Delete manually <ExternalLink className="size-3" />
              </a>
            )}
            {/* Only GitHub has a stored URL to null out via unlink -- see the
                per-row guard above. */}
            {error.resourceKey === "github" && error.urlFields && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUnlink(error.resourceKey!, error.urlFields!)}
                disabled={unlinking}
              >
                {unlinking ? "Unlinking..." : "Mark as removed"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Owners' way back after an Azure-only teardown of a project whose
          resource groups ScaffoldUI creates: its deploy cannot create them. */}
      {isOwner && project.deploy_identity_scope === "resource_group" && (
        <RedeploySection project={project} />
      )}

      {/* App registration requests -- same `canManageResources` gate as the
          rest of this tab, re-checked server-side on every call. */}
      <AppRegistrationRequestsSection
        projectId={project.id}
        projectName={project.project_name}
        canManage={isOwner}
      />

      {/* Remove Project button. Deleting the repository (and Azure) is not the
          whole delete: only this removes the project and what ScaffoldUI set up
          for it in Entra, so say so once it is the step left (project 55). */}
      {isOwner && (
        <div className="space-y-2 border-t pt-4">
          {removeProjectIsLastStep && (
            <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <strong className="font-medium">Last step: Remove Project.</strong> Until then the
                project stays in Scaffold UI, and so do its sign-in app registrations, deploy
                identity, Entra groups and any Azure resources that are left. If it still holds an
                app registration it asked for, that has to be decommissioned first (App
                registrations, above).
              </span>
            </p>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={!allResourcesRemoved}>
                <Trash2 className="mr-2 size-4" />
                Remove Project
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove project?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the project from Scaffold UI, with its Azure resources,
                  sign-in app registrations and deploy identity, and tries to delete its Entra
                  groups. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDeleteProject}
                >
                  Remove Project
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {!allResourcesRemoved && (
            <p className="text-muted-foreground text-xs">
              {repoGoneOutsideScaffold
                ? "The repository is already gone from GitHub: use Mark as removed on its row first."
                : "Delete the GitHub repository first."}{" "}
              Remove Project then deletes the rest, Azure resources included.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Destructive trigger for tearing down a project's Azure footprint (NOR-295).
 *
 * Double confirmation: opening the dialog is the first step; typing the
 * project's exact name enables the delete button (second step), which itself
 * performs the actual teardown call.
 */
function AzureTeardownTrigger({
  projectName,
  deleting,
  disabled,
  onConfirm,
}: {
  projectName: string;
  deleting: boolean;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const canConfirm = confirmText === projectName;

  return (
    <AlertDialog onOpenChange={(open) => !open && setConfirmText("")}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
          disabled={disabled}
        >
          <Trash2 className="mr-1 size-3.5" />
          {deleting ? "Deleting..." : "Delete"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Azure Resource Group?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the project&apos;s Azure resource group, its Entra SSO redirect
            URI, and its provisioned repo secrets. The deployed app will stop working. This cannot
            be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="azure-teardown-confirm" className="text-sm font-normal">
            Type <strong className="text-foreground font-semibold">{projectName}</strong> to
            confirm.
          </Label>
          <Input
            id="azure-teardown-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            Delete Azure Resources
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** A single row in the detail view. */
function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="text-muted-foreground size-4 shrink-0" />
      <span className="text-muted-foreground w-14 shrink-0">{label}</span>
      <span className="text-foreground flex items-center">{children}</span>
    </div>
  );
}
